import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { playDing } from "@/components/pos/OnlineOrderNotifier";

// 🔔 Boss/admin bell: POS sale blocked by insufficient stock (negative-stock switch OFF).
// Polls /api/inventory/stock-blocks every 10s, rings on new alerts, re-rings every 60s while open.
const TEXTS = {
  zh: { title: "🚫 前台缺货无法开单", need: "需要", have: "库存", cashier: "收银员", fix: "✏️ 调库存", done: "✔ 已处理", open: "个缺货告警", hide: "收起", again: "⏰ 缺货告警仍未处理", alreadyDone: "该告警已被其他人处理" },
  es: { title: "🚫 Caja bloqueada: sin stock", need: "Necesita", have: "Hay", cashier: "Cajero", fix: "✏️ Ajustar stock", done: "✔ Resuelto", open: "alerta(s) de stock", hide: "Ocultar", again: "⏰ Alerta de stock sin resolver", alreadyDone: "Esa alerta ya fue resuelta por otro usuario" },
  en: { title: "🚫 POS blocked: out of stock", need: "Needs", have: "Have", cashier: "Cashier", fix: "✏️ Adjust stock", done: "✔ Resolved", open: "stock alert(s)", hide: "Hide", again: "⏰ Stock alert still open", alreadyDone: "Already resolved by someone else" },
  pt: { title: "🚫 Caixa bloqueado: sem estoque", need: "Precisa", have: "Tem", cashier: "Caixa", fix: "✏️ Ajustar estoque", done: "✔ Resolvido", open: "alerta(s) de estoque", hide: "Ocultar", again: "⏰ Alerta de estoque sem resolver", alreadyDone: "Já resolvido por outro usuário" },
};
const cfg = () => TEXTS[localStorage.getItem("app_lang") || "zh"] || TEXTS.zh;

export default function StockBlockNotifier() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const knownRef = useRef(new Set());
  const seededRef = useRef(false);
  const lastRingRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/inventory/stock-blocks`, { params: { status: "open" } });
        if (cancelled) return;
        const list = r.data?.items || [];
        setItems(list);
        const t = cfg();
        const now = Date.now();
        const fresh = list.filter((a) => !knownRef.current.has(a.id));
        fresh.forEach((a) => knownRef.current.add(a.id));
        if (fresh.length > 0 && (seededRef.current || list.length > 0)) {
          setCollapsed(false);
          playDing("loud");
          lastRingRef.current = now;
          fresh.forEach((a) => {
            toast.error(`${t.title}: ${a.product_name}`, {
              description: `${t.need} ${Number(a.needed)} · ${t.have} ${Number(a.have)} · ${t.cashier}: ${a.cashier_name || "-"}`,
              duration: 20000,
            });
          });
        } else if (list.length > 0 && now - lastRingRef.current >= 60000) {
          playDing("loud");
          lastRingRef.current = now;
          toast.warning(`${t.again} (${list.length})`, { duration: 8000 });
        }
        seededRef.current = true;
      } catch { /* offline — retry next tick */ }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const resolve = async (a) => {
    try {
      await axios.put(`${API}/inventory/stock-blocks/${a.id}/resolve`);
      setItems((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e) {
      // 404 = another admin / PC already marked it — not an error for this user, just drop it from the panel
      if (e?.response?.status === 404) { setItems((prev) => prev.filter((x) => x.id !== a.id)); toast.success(cfg().alreadyDone); return; }
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (items.length === 0) return null;
  const t = cfg();
  if (collapsed) {
    return (
      <button onClick={() => setCollapsed(false)} data-testid="stock-block-collapsed"
        className="fixed bottom-20 right-4 z-[900] bg-red-600 text-white font-black px-4 py-3 rounded-full shadow-2xl animate-pulse">
        🔔 {items.length} {t.open}
      </button>
    );
  }
  return (
    <div className="fixed bottom-20 right-4 z-[900] w-[min(92vw,380px)] bg-red-700 border-4 border-red-300 rounded-2xl shadow-2xl text-white overflow-hidden" data-testid="stock-block-panel">
      <div className="flex items-center justify-between px-4 py-2 bg-red-800">
        <span className="font-black text-base">{t.title}</span>
        <button onClick={() => setCollapsed(true)} className="text-xs underline opacity-80" data-testid="stock-block-hide">{t.hide}</button>
      </div>
      <div className="max-h-[50vh] overflow-y-auto divide-y divide-red-500/60">
        {items.map((a) => (
          <div key={a.id} className="px-4 py-3 space-y-2" data-testid={`stock-block-${a.id}`}>
            <div className="font-black text-lg leading-tight">{a.product_code ? `[${a.product_code}] ` : ""}{a.product_name}</div>
            <div className="text-sm font-semibold">
              {t.need} <b className="text-yellow-200">{Number(a.needed)}</b> · {t.have} <b className="text-yellow-200">{Number(a.have)}</b>
              <span className="opacity-80"> · {t.cashier}: {a.cashier_name || "-"} · {(a.created_at || "").slice(11, 16)}</span>
            </div>
            {a.warehouse_name && <div className="text-xs font-bold text-yellow-200" data-testid={`stock-block-wh-${a.id}`}>🏬 {a.warehouse_name}{a.warehouse_is_main === false ? " (sucursal / 分店)" : ""}</div>}
            <div className="flex gap-2">
              <button onClick={() => navigate(a.warehouse_is_main === false && a.warehouse_id
                ? `/admin/warehouses?wh=${encodeURIComponent(a.warehouse_id)}&q=${encodeURIComponent(a.product_code || a.product_name || "")}&stock=1`
                : `/admin/products?q=${encodeURIComponent(a.product_code || a.product_name || "")}&stock=1`)}
                className="flex-1 bg-white text-red-700 font-black py-2 rounded-lg hover:bg-red-50" data-testid={`stock-block-fix-${a.id}`}>{t.fix}</button>
              <button onClick={() => resolve(a)}
                className="flex-1 bg-emerald-500 text-white font-black py-2 rounded-lg hover:bg-emerald-600" data-testid={`stock-block-resolve-${a.id}`}>{t.done}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
